// src/domains/dashboard/pages/professional/ProfilePage.jsx
// Configurações de perfil do profissional: foto, descrição, disponibilidade, domiciliar
import React, { useState, useEffect } from 'react';
import { useAuth } from 'contexts/AuthContext';
import { useApp } from 'contexts/AppContext';
import { usersService, authService } from 'infrastructure/firebase';
import { PhotoUpload, Button } from 'shared/components/ui';
import { useMobile } from 'shared/hooks/useMobile';

const DAYS_LABELS = ['Domingo','Segunda','Terça','Quarta','Quinta','Sexta','Sábado'];
const DAYS_SHORT  = ['Dom','Seg','Ter','Qua','Qui','Sex','Sáb'];

const T = {
  brand: '#6B5876', accent: '#C9929A', border: '#E8E5EC',
  s2: '#FAF7FB', s3: '#F0EDF3', text: '#3D3447', text2: '#6B6070', muted: '#A8A1B0',
  sans: "'DM Sans',sans-serif", serif: "'Instrument Serif',serif",
};

const inputStyle = {
  width: '100%', padding: '11px 14px', border: `1.5px solid ${T.border}`,
  borderRadius: 10, fontSize: 14, fontFamily: T.sans, color: T.text,
  outline: 'none', boxSizing: 'border-box', background: '#fff',
};

function ChangePasswordSection({ showToast }) {
  const [open, setOpen]         = useState(false);
  const [current, setCurrent]   = useState('');
  const [next, setNext]         = useState('');
  const [confirm, setConfirm]   = useState('');
  const [saving, setSaving]     = useState(false);
  const [pwError, setPwError]   = useState('');

  const reset = () => { setCurrent(''); setNext(''); setConfirm(''); setPwError(''); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setPwError('');
    if (!current)          { setPwError('Informe a senha atual.'); return; }
    if (next.length < 6)   { setPwError('Nova senha deve ter no mínimo 6 caracteres.'); return; }
    if (next === current)  { setPwError('A nova senha deve ser diferente da atual.'); return; }
    if (next !== confirm)  { setPwError('As senhas não coincidem.'); return; }
    setSaving(true);
    try {
      await authService.changePassword(current, next);
      showToast('Senha alterada com sucesso!');
      reset();
      setOpen(false);
    } catch (err) {
      const code = err?.code || '';
      if (code === 'auth/wrong-password' || code === 'auth/invalid-credential') {
        setPwError('Senha atual incorreta. Tente novamente.');
      } else if (code === 'auth/too-many-requests') {
        setPwError('Muitas tentativas. Aguarde alguns minutos.');
      } else {
        setPwError('Erro ao alterar senha. Tente novamente.');
      }
    } finally { setSaving(false); }
  };

  return (
    <div style={{ background: '#fff', border: `1px solid ${T.border}`, borderRadius: 16,
      overflow: 'hidden', marginBottom: 20 }}>
      <button onClick={() => { setOpen(o => !o); reset(); }}
        style={{ width: '100%', padding: '18px 24px', background: 'none', border: 'none',
          cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          fontFamily: T.sans }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, color: T.muted, textTransform: 'uppercase',
            letterSpacing: 1, textAlign: 'left' }}>Segurança</div>
          <div style={{ fontSize: 13, color: T.text2, marginTop: 2, textAlign: 'left' }}>Alterar senha da conta</div>
        </div>
        <span style={{ color: T.muted, fontSize: 18, transform: open ? 'rotate(180deg)' : 'none',
          transition: 'transform 0.2s' }}>▾</span>
      </button>

      {open && (
        <form onSubmit={handleSubmit} style={{ padding: '0 24px 24px', borderTop: `1px solid ${T.border}` }}>
          <div style={{ paddingTop: 18 }}>
            {pwError && (
              <div style={{ padding: '10px 14px', background: '#F0DADD', borderRadius: 8,
                color: '#7A3C44', fontSize: 13, marginBottom: 14 }}>{pwError}</div>
            )}
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: T.text2, marginBottom: 5 }}>
                Senha atual
              </label>
              <input type="password" value={current} onChange={e => setCurrent(e.target.value)}
                required placeholder="••••••••" autoComplete="current-password" style={inputStyle} />
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: T.text2, marginBottom: 5 }}>
                Nova senha <span style={{ fontSize: 11, color: T.muted, fontWeight: 400 }}>(mín. 6 caracteres)</span>
              </label>
              <input type="password" value={next} onChange={e => setNext(e.target.value)}
                required placeholder="••••••••" autoComplete="new-password" style={inputStyle} />
            </div>
            <div style={{ marginBottom: 18 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: T.text2, marginBottom: 5 }}>
                Confirmar nova senha
              </label>
              <input type="password" value={confirm} onChange={e => setConfirm(e.target.value)}
                required placeholder="••••••••" autoComplete="new-password"
                style={{ ...inputStyle, borderColor: confirm && next !== confirm ? T.accent : T.border }} />
              {confirm && next !== confirm && (
                <span style={{ fontSize: 11, color: T.accent, marginTop: 3, display: 'block' }}>
                  As senhas não coincidem
                </span>
              )}
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <Button type="button" variant="secondary" onClick={() => { setOpen(false); reset(); }}>
                Cancelar
              </Button>
              <Button type="submit" variant="primary" disabled={saving} loading={saving}>
                {saving ? 'Salvando...' : 'Alterar senha'}
              </Button>
            </div>
          </div>
        </form>
      )}
    </div>
  );
}

export default function ProfessionalProfilePage() {
  const isMobile = useMobile();
  const { userProfile } = useAuth();
  const { showToast }   = useApp();

  const [form, setForm] = useState({
    photo:                '',
    descricaoProfissional:'',
    specialty:            '',
    diasDisponiveis:      [1,2,3,4,5], // seg-sex por padrão
    atendimentoDomiciliar:false,
    endereco:             '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (userProfile) {
      setForm({
        photo:                userProfile.photo                || '',
        descricaoProfissional:userProfile.descricaoProfissional|| '',
        specialty:            userProfile.specialty            || '',
        diasDisponiveis:      userProfile.diasDisponiveis      || [1,2,3,4,5],
        atendimentoDomiciliar:userProfile.atendimentoDomiciliar|| false,
        endereco:             userProfile.endereco             || '',
      });
    }
  }, [userProfile]);

  const toggleDay = (dow) => setForm(f => {
    const has  = f.diasDisponiveis.includes(dow);
    const next = has
      ? f.diasDisponiveis.filter(d => d !== dow)
      : [...f.diasDisponiveis, dow].sort();
    return { ...f, diasDisponiveis: next };
  });

  const handleSave = async () => {
    if (!userProfile?.id) return;
    setSaving(true);
    try {
      await usersService.update(userProfile.id, {
        photo:                form.photo        || null,
        descricaoProfissional:form.descricaoProfissional.slice(0, 500),
        specialty:            form.specialty,
        diasDisponiveis:      form.diasDisponiveis,
        atendimentoDomiciliar:form.atendimentoDomiciliar,
        endereco:             !form.atendimentoDomiciliar ? form.endereco : '',
      });
      showToast('Perfil atualizado!');
    } catch {
      showToast('Erro ao salvar. Tente novamente.', 'danger');
    } finally { setSaving(false); }
  };

  return (
    <div style={{ maxWidth: 600, fontFamily: T.sans }}>
      <div style={{ marginBottom: 28 }}>
        <div style={{ fontFamily: T.serif, fontSize: 28, color: T.text }}>Meu Perfil</div>
        <div style={{ fontSize: 13, color: T.muted, marginTop: 4 }}>
          Configure seu perfil público — clientes verão estas informações ao contratar você.
        </div>
      </div>

      {/* Foto + identidade */}
      <div style={{ background: '#fff', border: `1px solid ${T.border}`, borderRadius: 16,
        padding: 24, marginBottom: 20 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: T.muted, textTransform: 'uppercase',
          letterSpacing: 1, marginBottom: 16 }}>
          Foto e identidade
        </div>
        <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start',
          flexDirection: isMobile ? 'column' : 'row' }}>
          <PhotoUpload
            value={form.photo}
            onChange={v => setForm(f => ({ ...f, photo: v }))}
            emoji="👤"
            shape="circle"
          />
          <div style={{ flex: 1 }}>
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: T.text2, marginBottom: 5 }}>
                Especialidade / Cargo
              </label>
              <input value={form.specialty}
                onChange={e => setForm(f => ({ ...f, specialty: e.target.value }))}
                placeholder="Ex: Cabeleireiro, Dermatologista, Dentista..."
                style={inputStyle} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: T.text2, marginBottom: 5 }}>
                Descrição profissional{' '}
                <span style={{ fontSize: 11, color: form.descricaoProfissional.length > 450 ? T.accent : T.muted, fontWeight: 400 }}>
                  ({form.descricaoProfissional.length}/500)
                </span>
              </label>
              <textarea value={form.descricaoProfissional}
                onChange={e => setForm(f => ({ ...f, descricaoProfissional: e.target.value.slice(0, 500) }))}
                rows={3} placeholder="Conte sua experiência, formação e diferenciais..."
                style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.6 }} />
            </div>
          </div>
        </div>
      </div>

      {/* Disponibilidade para contratação */}
      <div style={{ background: '#fff', border: `1px solid ${T.border}`, borderRadius: 16,
        padding: 24, marginBottom: 20 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: T.muted, textTransform: 'uppercase',
          letterSpacing: 1, marginBottom: 4 }}>
          Disponibilidade para contratação
        </div>
        <div style={{ fontSize: 12, color: T.muted, marginBottom: 16 }}>
          Dias da semana em que você está disponível para novas contratações.
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 6 }}>
          {[0,1,2,3,4,5,6].map(dow => {
            const active = form.diasDisponiveis.includes(dow);
            return (
              <button key={dow} onClick={() => toggleDay(dow)} style={{
                padding: '10px 4px', borderRadius: 10,
                border: `2px solid ${active ? T.brand : T.border}`,
                background: active ? T.brand : T.s2, color: active ? '#fff' : T.muted,
                fontSize: 11, fontWeight: 700, cursor: 'pointer', transition: 'all .15s',
              }}>
                {DAYS_SHORT[dow]}
              </button>
            );
          })}
        </div>
        {form.diasDisponiveis.length > 0 && (
          <div style={{ marginTop: 10, fontSize: 12, color: T.muted }}>
            Disponível: {form.diasDisponiveis.map(d => DAYS_LABELS[d]).join(', ')}
          </div>
        )}
      </div>

      {/* Atendimento domiciliar */}
      <div style={{ background: '#fff', border: `1px solid ${T.border}`, borderRadius: 16,
        padding: 24, marginBottom: 28 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: T.muted, textTransform: 'uppercase',
          letterSpacing: 1, marginBottom: 16 }}>
          Atendimento domiciliar
        </div>

        <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
          {[
            { value: true,  label: 'Sim — atendo na residência do cliente' },
            { value: false, label: 'Não — atendo em local fixo' },
          ].map(opt => (
            <button key={String(opt.value)} onClick={() => setForm(f => ({ ...f, atendimentoDomiciliar: opt.value }))}
              style={{ flex: 1, padding: '11px 12px', borderRadius: 10, cursor: 'pointer',
                border: `2px solid ${form.atendimentoDomiciliar === opt.value ? T.brand : T.border}`,
                background: form.atendimentoDomiciliar === opt.value ? T.s3 : '#fff',
                color: form.atendimentoDomiciliar === opt.value ? T.brand : T.text2,
                fontSize: 13, fontWeight: form.atendimentoDomiciliar === opt.value ? 600 : 400,
                fontFamily: T.sans, textAlign: 'left', transition: 'all 0.15s' }}>
              {opt.value ? '🏠' : '📍'} {opt.label}
            </button>
          ))}
        </div>

        {/* Endereço — exibe apenas se NÃO faz domiciliar */}
        {!form.atendimentoDomiciliar && (
          <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: T.text2, marginBottom: 5 }}>
              Endereço de atendimento
            </label>
            <input value={form.endereco}
              onChange={e => setForm(f => ({ ...f, endereco: e.target.value }))}
              placeholder="Rua, número, bairro, cidade"
              style={inputStyle} />
          </div>
        )}
      </div>

      <Button variant="primary" loading={saving} onClick={handleSave}
        style={{ width: '100%', padding: '14px 0', fontSize: 15 }}>
        💾 Salvar Perfil
      </Button>

      <div style={{ marginTop: 28 }}>
        <ChangePasswordSection showToast={showToast} />
      </div>
    </div>
  );
}
